import { useEffect, useReducer, useRef } from 'react';
import { produce } from 'immer';
import { type PairingV1 as Pairing } from '@hungryroot/rest';
import { DrawerModal } from '../DrawerModal/DrawerModal';
import {
  LikeDislike as LikeDislikeCmp,
  LikeDislikeSize,
  LikeDislikeVariant,
} from '../LikeDislike/LikeDislike';
import { RatingSheet } from '../RatingSheet/RatingSheet';
import {
  ApiV1LikeDislikeEnum as LikeDislike,
  setPairingRating,
  type LikeDislikeRatings,
  type UserDefinedRating,
} from './api';

interface Props {
  testid?: string;
  rating: LikeDislikeRatings;
  pairing: Pick<Pairing, 'id' | 'name' | 'ingredients'>;
  orderId: number;
  className?: string;
  onRatingSheetClosed?: () => void;
  onRatingChange?: (rating: UserDefinedRating) => void;
}

type ModalState =
  | {
      show: true;
      rating: UserDefinedRating;
    }
  | {
      show: false;
      rating: LikeDislikeRatings;
    };

interface State {
  modal: ModalState;
  user: {
    rating: UserDefinedRating;
    feedback?: string;
  } | null;
}

interface BinaryRatingActions {
  User: {
    DidPersistSelection: {
      type: 'User.DidPersistSelection';
      feedback?: string;
      rating: UserDefinedRating;
    };
  };
  LikeDislike: {
    RatingClicked: {
      type: 'LikeDislike.RatingClicked';
      rating: UserDefinedRating;
    };
  };
  Feedback: {
    RatingClicked: {
      type: 'Feedback.RatingClicked';
      rating: UserDefinedRating;
    };
    FeedbackSubmitted: {
      type: 'Feedback.FeedbackSubmitted';
      feedback: string;
      rating: UserDefinedRating;
    };
  };
  Modal: {
    RequestDismiss: {
      type: 'Modal.RequestDismiss';
    };
  };
}

type Actions =
  | BinaryRatingActions['User'][keyof BinaryRatingActions['User']]
  | BinaryRatingActions['Modal'][keyof BinaryRatingActions['Modal']]
  | BinaryRatingActions['LikeDislike'][keyof BinaryRatingActions['LikeDislike']]
  | BinaryRatingActions['Feedback'][keyof BinaryRatingActions['Feedback']];

export function BinaryRatings(props: Props) {
  const {
    testid,
    className,
    rating: initialRating,
    pairing,
    orderId,
    onRatingSheetClosed,
    onRatingChange,
  } = props;
  const focusRef = useRef<HTMLTextAreaElement>(null);
  const didMountRef = useRef(false);
  const [state, dispatch] = useReducer(
    (state: State, action: Actions) => {
      switch (action.type) {
        case 'Modal.RequestDismiss':
          return produce(state, (draft) => {
            draft.modal.show = false;
          });
        case 'Feedback.FeedbackSubmitted':
          return produce(state, (draft) => {
            const { rating, feedback } = action;
            draft.user = {
              rating,
              feedback,
            };
          });
        case 'User.DidPersistSelection':
          return produce(state, (draft) => {
            const { feedback } = action;
            draft.user = null;
            // User persisted feedback, close modal
            if (feedback !== undefined) {
              draft.modal.show = false;
            }
          });
        case 'Feedback.RatingClicked':
          return produce(state, (draft) => {
            const { user } = draft;
            const { rating } = action;
            if (user === null) {
              draft.user = {
                rating,
              };
            } else {
              user.rating = rating;
            }
            draft.modal = {
              show: true,
              rating,
            };
          });
        case 'LikeDislike.RatingClicked':
          return produce(state, (draft) => {
            const { rating } = action;
            if (rating !== initialRating) {
              draft.user = {
                rating,
              };
            }
            draft.modal = {
              show: true,
              rating,
            };
          });
      }
    },
    {
      user: null,
      modal: {
        show: false,
        rating: initialRating,
      },
    }
  );

  const { modal, user: userSelectedRating } = state;
  const { rating, show: modalIsVisible } = modal;
  const { id: pairingId } = pairing;

  useEffect(() => {
    if (onRatingSheetClosed === undefined || modal.show === true) {
      return;
    }

    if (didMountRef.current === true) {
      onRatingSheetClosed();
    }
    didMountRef.current = true;
  }, [modal.show]);

  useEffect(() => {
    if (userSelectedRating === null) {
      return;
    }

    if (onRatingChange !== undefined) {
      onRatingChange(userSelectedRating.rating);
    }

    const { rating: userLiked, feedback: userFeedback } = userSelectedRating;

    let mounted = true;
    setPairingRating({
      orderId,
      pairingId,
      feedback: userFeedback === undefined ? '' : userFeedback,
      liked: userLiked,
    }).then(() => {
      if (mounted === false) {
        return;
      }
      dispatch({
        type: 'User.DidPersistSelection',
        feedback: userFeedback,
        rating: userLiked,
      });
    });
    return () => {
      mounted = false;
    };
  }, [userSelectedRating]);

  return (
    <div
      data-request-in-flight={state.user !== null}
      className={className}
      data-testid={testid}
    >
      <LikeDislikeCmp
        onRating={(updatedRating) => {
          if (updatedRating === LikeDislike.NoRating) {
            // don't let user un-rate here.
            dispatch({
              type: 'LikeDislike.RatingClicked',
              rating,
            });
            return;
          }
          dispatch({
            type: 'LikeDislike.RatingClicked',
            rating: updatedRating as LikeDislikeRatings,
          });
        }}
        hideUnselected
        size={LikeDislikeSize.Small}
        rating={rating}
        testid={`${testid}-like-dislike`}
        variant={LikeDislikeVariant.Pairing}
      />
      <DrawerModal
        testid={`${testid}-ratings-modal`}
        onRequestDismiss={() =>
          dispatch({
            type: 'Modal.RequestDismiss',
          })
        }
        initialFocusRef={focusRef}
        ariaLabel="Feedback Modal"
        active={modalIsVisible}
      >
        {rating !== LikeDislike.NoRating && (
          <RatingSheet
            textareaRef={focusRef}
            onRating={(updatedRating) => {
              dispatch({
                type: 'Feedback.RatingClicked',
                rating: updatedRating,
              });
            }}
            onFeedbackSubmit={({ feedback: userFeedback }) => {
              dispatch({
                type: 'Feedback.FeedbackSubmitted',
                feedback: userFeedback,
                rating,
              });
            }}
            pairing={pairing}
            rating={rating}
            testid={`${testid}-rating-sheet`}
          />
        )}
      </DrawerModal>
    </div>
  );
}
